import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  MonitorDown,
  Settings2,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { downloadFile } from "@telegram-apps/sdk-react";
import { dataTrigger } from "@/analytics";
import { appErrorMessage, createAppLink } from "@/api/app";
import { useAppFeatures } from "@/store/appFeatures";
import type { Trigger } from "@/analytics/triggers";
import WavesBackground from "@/components/WavesBackground/WavesBackground";
import { ShieldLogo } from "@/components/ShieldLogo/ShieldLogo";
import { Button } from "@/ui/Button/Button";
import {
  buildVpnImportAppLink,
  VpnImportApp,
} from "@/components/VpnImportActions/lib";
import { InstructionScreen } from "@/features/InstructionScreen/InstructionScreen";
import cls from "./styles.module.scss";

type PlatformKey = "ios" | "android" | "macos" | "windows" | "linux" | "other";

type PlatformInfo = {
  key: PlatformKey;
  label: string;
  hint: string;
  downloadUrl: string;
  importApp: VpnImportApp;
};

type StepItem = {
  title: string;
  description: string;
  note: string;
  actionLabel: string;
  actionIcon: React.ReactNode;
  actionTrigger: Trigger;
  onAction: () => void;
};

const PLATFORM_MAP: Record<PlatformKey, PlatformInfo> = {
  ios: {
    key: "ios",
    label: "iPhone / iPad",
    hint: "Откройте страницу Happ в App Store.",
    downloadUrl:
      "https://apps.apple.com/ru/app/happ-proxy-utility-plus/id6746188973",
    importApp: "happ",
  },
  android: {
    key: "android",
    label: "Android",
    hint: "Откройте Google Play и установите v2RayTun.",
    downloadUrl:
      "https://play.google.com/store/apps/details?id=com.v2raytun.android",
    importApp: "v2raytun",
  },
  macos: {
    key: "macos",
    label: "macOS",
    hint: "Откройте страницу v2RayTun для macOS.",
    downloadUrl: "https://apps.apple.com/jp/app/v2raytun/id6476628951?l=en-US",
    importApp: "v2raytun",
  },
  windows: {
    key: "windows",
    label: "Windows",
    hint: "Откройте официальную страницу загрузки v2RayTun.",
    downloadUrl: "https://v2raytun.com",
    importApp: "v2raytun",
  },
  linux: {
    key: "linux",
    label: "Linux",
    hint: "Откройте официальную страницу загрузки v2RayTun.",
    downloadUrl: "https://v2raytun.com",
    importApp: "v2raytun",
  },
  other: {
    key: "other",
    label: "Ваше устройство",
    hint:
      "Откройте официальную страницу v2RayTun и выберите платформу вручную.",
    downloadUrl: "https://v2raytun.com",
    importApp: "v2raytun",
  },
};

/**
 * БКоннект — наше приложение. На Windows вместо v2RayTun: установщик с домена мини-аппа
 * (/download/), второй шаг — ссылка bkvpn://login/<билет>, по ней приложение входит в
 * аккаунт само. На Android — так же, APK с /download/, но только когда в админке включён
 * выключатель «Приложение для Android в мини-аппе» (features.android_app). На остальных
 * платформах — как раньше.
 */
type BkVpnArch = "x64" | "arm64";

const BKVPN_FILES: Record<BkVpnArch, string> = {
  x64: "BKonnekt-setup-x64.exe",
  arm64: "BKonnekt-setup-arm64.exe",
};

/** Внутри Telegram — его окно «Скачать файл», иначе — браузер. */
async function downloadFromSite(fileName: string) {
  const url = new URL(`/download/${fileName}`, window.location.origin).href;
  try {
    if (downloadFile.isAvailable()) {
      await downloadFile(url, fileName);
      return;
    }
  } catch {
    return; // человек отказался в окне Telegram — браузер не открываем
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

const downloadBkVpn = (arch: BkVpnArch) => downloadFromSite(BKVPN_FILES[arch]);

/** APK для Android: постоянное имя, на сервере всегда последняя версия. */
const ANDROID_APK = "BKonnekt-android.apk";

function getPlatform(): PlatformInfo {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) return PLATFORM_MAP.ios;
  if (userAgent.includes("android")) return PLATFORM_MAP.android;
  if (userAgent.includes("mac os x")) return PLATFORM_MAP.macos;
  if (userAgent.includes("windows")) return PLATFORM_MAP.windows;
  if (userAgent.includes("linux")) return PLATFORM_MAP.linux;

  return PLATFORM_MAP.other;
}

export default function InstallV2RayTunPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);

  const configUrl = searchParams.get("url");
  const platform = useMemo(() => getPlatform(), []);
  const appLink = useMemo(() => {
    if (!configUrl) return null;
    return buildVpnImportAppLink(platform.importApp, configUrl);
  }, [configUrl, platform]);
  const androidApp = useAppFeatures((s) => !!s.android_app);
  const loadFeatures = useAppFeatures((s) => s.load);
  useEffect(() => {
    if (platform.key === "android") void loadFeatures();
  }, [platform.key, loadFeatures]);
  const isAndroidApp = platform.key === "android" && androidApp;
  const isBkVpn = platform.key === "windows" || isAndroidApp;
  // Кнопка входа в приложении: в Windows — «Открыть Telegram», в Android — «Войти через Telegram».
  const tgButton = isAndroidApp ? "Войти через Telegram" : "Открыть Telegram";
  const appName = isBkVpn
    ? "БКоннект"
    : platform.importApp === "happ"
      ? "Happ"
      : "v2RayTun";
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkNote, setLinkNote] = useState<string | null>(null);

  const addToBkVpn = async () => {
    if (linkBusy) return;
    setLinkBusy(true);
    setLinkNote(null);
    try {
      const { link } = await createAppLink();
      window.open(link, "_blank", "noopener,noreferrer");
      setLinkNote(
        "Если приложение не открылось — установите его (шаг 1) и нажмите кнопку ещё раз."
      );
    } catch (error) {
      setLinkNote(
        appErrorMessage(
          error,
          "Не получилось подготовить вход. Попробуйте ещё раз.",
          `Вход по кнопке сейчас выключен. Откройте приложение БКоннект и нажмите «${tgButton}».`
        )
      );
    } finally {
      setLinkBusy(false);
    }
  };

  const bkVpnSteps: StepItem[] = [
    isAndroidApp
      ? {
          title: "Скачайте БКоннект",
          description:
            "Наше приложение для Android: одна кнопка «Подключить», без настроек.",
          note: "Откройте скачанный файл. Android спросит разрешение на установку из этого источника — разрешите.",
          actionLabel: "Скачать для Android",
          actionIcon: <Download />,
          actionTrigger: "bkn.install.bkvpn_download_android",
          onAction: () => {
            void downloadFromSite(ANDROID_APK);
          },
        }
      : {
          title: "Скачайте БКоннект",
          description:
            "Наше приложение для Windows: одна кнопка «Подключить», без настроек.",
          note: "Если Windows предупредит о файле — «Подробнее» → «Выполнить в любом случае».",
          actionLabel: "Скачать для Windows",
          actionIcon: <Download />,
          actionTrigger: "bkn.install.bkvpn_download",
          onAction: () => {
            void downloadBkVpn("x64");
          },
        },
    {
      title: "Добавьте в приложение",
      description:
        "Установите БКоннект и нажмите кнопку — приложение откроется и войдёт в ваш аккаунт само.",
      note: linkNote ?? "Ссылка одноразовая и действует 10 минут.",
      actionLabel: linkBusy ? "Готовим вход…" : "Добавить в приложение",
      actionIcon: <MonitorDown />,
      actionTrigger: "bkn.install.bkvpn_add",
      onAction: () => {
        void addToBkVpn();
      },
    },
    {
      title: "Готово",
      description:
        "Нажмите «Подключить» в приложении БКоннект. Подписка подтянется сама — ссылки и конфиги вводить не нужно.",
      note: `Не получилось? Откройте приложение БКоннект и нажмите «${tgButton}».`,
      actionLabel: "На главную",
      actionIcon: <CheckCircle2 />,
      actionTrigger: "bkn.install.done_home",
      onAction: () => {
        window.location.href = "/";
      },
    },
  ];

  const importSteps: StepItem[] = [
    {
      title: `Скачайте ${appName}`,
      description: platform.hint,
      note: `Платформа: ${platform.label}`,
      actionLabel: `Скачать для ${platform.label}`,
      actionIcon: <Download />,
      actionTrigger: "bkn.install.download",
      onAction: () => {
        window.open(platform.downloadUrl, "_blank", "noopener,noreferrer");
      },
    },
    {
      title: "Добавьте конфиг",
      description: `После установки вернитесь сюда и импортируйте конфигурацию в ${appName}.`,
      note: "Кнопка ниже откроет приложение и передаст конфиг автоматически.",
      actionLabel: "Добавить конфиг",
      actionIcon: <Settings2 />,
      actionTrigger: "bkn.install.add_config",
      onAction: () => {
        if (!appLink) return;
        window.open(appLink, "_blank", "noopener,noreferrer");
      },
    },
    {
      title: "Готово",
      description: `Настройка завершена. Откройте ${appName} и подключитесь через импортированный профиль.`,
      note: "Если потребуется, шаги можно повторить позже.",
      actionLabel: "На главную",
      actionIcon: <CheckCircle2 />,
      actionTrigger: "bkn.install.done_home",
      onAction: () => {
        window.location.href = "/";
      },
    },
  ];

  const stepItems = isBkVpn ? bkVpnSteps : importSteps;

  if (showInstructions) {
    return (
      <InstructionScreen
        onBack={() => setShowInstructions(false)}
        subtitle="Выберите устройство для установки"
      />
    );
  }

  if (!configUrl || (!isBkVpn && !appLink)) {
    return (
      <div className={cls.container}>
        <WavesBackground />
        <div className={cls.shell}>
          <div className={cls.topbar}>
            <Link to="/" className={cls.backLink}>
              <ArrowLeft size={16} />
              Назад
            </Link>
          </div>

          <section className={cls.page}>
            <div className={cls.logoWrap}>
              <ShieldLogo />
            </div>
            <h1 className={cls.title}>Отсутствует ссылка на конфиг</h1>
            <p className={cls.description}>
              Не удалось подготовить установку {appName} для этой подписки.
            </p>
          </section>
        </div>
      </div>
    );
  }

  const currentStep = stepItems[step];
  const isFirstStep = step === 0;
  const isLastStep = step === stepItems.length - 1;

  return (
    <div className={cls.container}>
      <WavesBackground />

      <div className={cls.shell}>
        <div className={cls.topbar}>
          <Link to="/" className={cls.backLink}>
            <ArrowLeft size={16} />
            Назад
          </Link>

          <div className={cls.progress}>
            {stepItems.map((item, index) => (
              <button
                key={item.title}
                type="button"
                className={[
                  cls.progressDot,
                  index === step ? cls.progressDotActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setStep(index)}
                aria-label={`Перейти к шагу ${index + 1}`}
                {...dataTrigger("bkn.install.step_dot")}
              />
            ))}
          </div>
        </div>

        <section className={cls.page} key={step}>
          <div className={cls.logoWrap}>
            <ShieldLogo />
          </div>

          <h1 className={cls.title}>{currentStep.title}</h1>
          <p className={cls.description}>{currentStep.description}</p>
          <p className={cls.note}>{currentStep.note}</p>

          <Button
            variant="primary"
            size="lg"
            leftIcon={currentStep.actionIcon}
            onClick={currentStep.onAction}
            {...dataTrigger(currentStep.actionTrigger)}
          >
            {currentStep.actionLabel}
          </Button>

          {platform.key === "windows" && isFirstStep && (
            <button
              type="button"
              className={cls.textLink}
              onClick={() => {
                void downloadBkVpn("arm64");
              }}
              {...dataTrigger("bkn.install.bkvpn_download_arm")}
            >
              Компьютер на ARM? Скачать версию для ARM
            </button>
          )}

          {isFirstStep && (
            <Button
              variant="secondary"
              size="md"
              leftIcon={<BookOpen />}
              onClick={() => setShowInstructions(true)}
              {...dataTrigger("bkn.install.other_devices")}
            >
              Другие устройства
            </Button>
          )}

          <div className={cls.bottomRow}>
            <button
              type="button"
              className={cls.navButton}
              disabled={isFirstStep}
              onClick={() => setStep((value) => Math.max(value - 1, 0))}
              {...dataTrigger("bkn.install.step_prev")}
            >
              <ChevronLeft size={18} />
              Назад
            </button>

            {!isLastStep ? (
              <button
                type="button"
                className={cls.navButton}
                onClick={() =>
                  setStep((value) => Math.min(value + 1, stepItems.length - 1))}
                {...dataTrigger("bkn.install.step_next")}
              >
                Далее
                <ChevronRight size={18} />
              </button>
            ) : (
              <button
                type="button"
                className={cls.navButton}
                onClick={() => {
                  window.location.href = "/";
                }}
                {...dataTrigger("bkn.install.close")}
              >
                Закрыть
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
